import { getCurrencyFromLocale } from '@repo/utils'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import React from 'react'
import { ControllerRenderProps, FieldValues } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import { budgetMap } from './constants'
import { Budget, BudgetRange } from './types'
import { useTravelTypeContext } from '../../contexts'
import { useLocale } from '../../../hooks/use-locale'
import { CurrencyEnum, TravelTypeEnum } from '../../../lib/graphql'
import { cn } from '../../../shadcn/utils'
import { roundedPrice } from '../../../utils'
import { multiplyByFactor } from '../../../utils/prices'

type BudgetPickerContentModalProps = {
  field: ControllerRenderProps<FieldValues, string>
  onCloseModal?: () => void
  onValid?: () => void
}

type BudgetItemProps = {
  label: React.ReactNode
  budgetType: keyof Budget
  onClick: React.MouseEventHandler<HTMLDivElement>
  isPopular?: boolean
  isActive?: boolean
}

const getBudgetLimit = (travelType: TravelTypeEnum) =>
  travelType === TravelTypeEnum.NIGHTLY_TRIP ? 500 : 250

const getBudget = (travelType: TravelTypeEnum): Budget => {
  const budget = budgetMap.get(travelType)
  if (!budget) {
    throw new Error(`No budget found for travel type ${travelType}`)
  }
  return budget
}

const getBudgetItemLabel = ({
  values,
  budgetType,
  currency,
  travelType,
}: {
  budgetType: keyof Budget
  currency: CurrencyEnum
  values: BudgetRange
  travelType: TravelTypeEnum
}): React.ReactNode => {
  const budgetLimit = getBudgetLimit(travelType)

  return (
    <>
      {budgetLabels(budgetType)}:&nbsp;
      <FormattedMessage
        id='ui.BudgetItem.price'
        defaultMessage='{useMax, select,
    true {{min} à {max}}
    other {{min} et plus}
}'
        values={{
          useMax: values.budgetMax < budgetLimit,
          min: roundedPrice(multiplyByFactor(values.budgetMin), { currency }),
          max: roundedPrice(multiplyByFactor(values.budgetMax), { currency }),
        }}
      />
    </>
  )
}

const budgetLabels = (budgetType: keyof Budget) => {
  switch (budgetType) {
    case 'Economy':
      return (
        <FormattedMessage
          id='ui.BudgetPickerContentModal.economy'
          defaultMessage='Economique'
        />
      )
    case 'Standard':
      return (
        <FormattedMessage
          id='ui.BudgetPickerContentModal.standard'
          defaultMessage='Standard'
        />
      )
    case 'Premium':
      return (
        <FormattedMessage
          id='ui.BudgetPickerContentModal.premium'
          defaultMessage='Premium'
        />
      )
  }
}

const BudgetItem: React.FC<BudgetItemProps> = ({
  label,
  budgetType,
  onClick,
  isPopular,
  isActive,
}) => (
  <div
    className={cn(
      'group flex cursor-pointer flex-col gap-1 rounded-[4px] p-3 transition-colors hover:bg-grey-300',
      isActive && 'bg-grey-300',
    )}
    onClick={onClick}
    aria-label={'budget option ' + budgetType}
    data-testid={`budget-picker-option-${isPopular}`}
  >
    <span>{label}</span>
    {isPopular && (
      <div className='flex w-fit items-center justify-center rounded-[4px] border border-transparent bg-green-100 px-2 py-1 text-xs leading-none text-green-500 transition group-hover:border-green-300'>
        <FormattedMessage
          id='ui.BudgetPickerContentModal.popular'
          defaultMessage='Le plus populaire'
        />
      </div>
    )}
  </div>
)

export const BudgetPickerContentModal: React.FC<
  BudgetPickerContentModalProps
> = ({ field, onCloseModal, onValid }) => {
  const { travelType } = useTravelTypeContext()
  const { locale } = useLocale()

  const { currency } = getCurrencyFromLocale(locale)
  const budget = getBudget(travelType)

  const onClickItem = (values: BudgetRange) => (_event: React.MouseEvent) => {
    field.onChange({
      budgetMin: values.budgetMin,
      budgetMax: values.budgetMax,
    })

    onValid?.()
    onCloseModal?.()
  }

  return (
    <div>
      <h4 className='mb-3 w-full'>
        <FormattedMessage
          id='ui.BudgetPickerContentModal.title'
          defaultMessage='<strong>Votre budget HT</strong> /personne /nuit'
          values={{
            strong: (chunks) => <strong>{chunks}</strong>,
          }}
        />
      </h4>

      <div className='flex flex-col'>
        {Object.entries(budget).map(([key, values]) => {
          return (
            <BudgetItem
              key={`budget-${key}-${travelType}`}
              budgetType={key as keyof Budget}
              label={getBudgetItemLabel({
                currency,
                budgetType: key as keyof Budget,
                values,
                travelType,
              })}
              onClick={onClickItem(values)}
              isPopular={Boolean(values?.isPopular)}
              isActive={isEqual(field?.value, omit(values, 'isPopular'))}
            />
          )
        })}
      </div>
    </div>
  )
}
